import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  enrolmentId: number;
  studentId: string;
};

function RestoreEnrolmentModal({ enrolmentId, studentId, isOpen, onClose }: Props) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const methods = useForm<{ text: string; notifyStudentAndConsultant: boolean }>({
    defaultValues: { text: '', notifyStudentAndConsultant: true },
  });

  const resetFieldsAndClose = () => {
    methods.reset();
    onClose();
  };

  const restoreEnrolment = trpcAdmin.enrolment.restoreCourseEnrolment.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: t`Course enrolment has been restored`,
      });
      await Promise.all([
        utils.enrolment.byIdWithResourceStatus.invalidate({ enrolmentId }),
        utils.enrolment.list.invalidate(undefined, { refetchType: 'active' }),
        utils.students.adminNotes.invalidate(),
      ]);
      resetFieldsAndClose();
    },
    onError(error) {
      toast({
        status: 'error',
        title: error.message || t`An error occurred while restoring the course enrolment`,
      });
    },
  });

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={resetFieldsAndClose}>
      <ModalOverlay />
      <ModalContent
        padding={4}
        as="form"
        onSubmit={methods.handleSubmit((data) => {
          restoreEnrolment.mutate({
            enrolmentId,
            studentId,
            noteText: data.text,
            notifyStudentAndConsultant: data.notifyStudentAndConsultant,
          });
        })}
      >
        <ModalHeader>
          <Trans>Restore enrolment</Trans>
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={6}>
          <VStack gap={2} align="stretch">
            <FormControl isInvalid={methods.formState.errors.text != null}>
              <FormLabel
                color="fg.emphasis"
                fontWeight="medium"
                fontSize="sm"
                mb={1}
                htmlFor="text"
              >
                <Trans>Notes</Trans>
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              </FormLabel>
              <Controller
                name="text"
                rules={{
                  validate: (value) => value.trim() !== '' || t`Notes required`,
                }}
                control={methods.control}
                render={({ field }) => <Textarea id="text" rows={5} {...field} />}
              />
              <FormErrorMessage>{methods.formState.errors.text?.message}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <Controller
                name="notifyStudentAndConsultant"
                control={methods.control}
                render={({ field }) => (
                  <Checkbox
                    name="notifyStudentAndConsultant"
                    isChecked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    <Text textStyle="sm" color="fg.muted">
                      <Trans>Notify student and consultant</Trans>
                    </Text>
                  </Checkbox>
                )}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="button" variant="ghost" colorScheme="black" onClick={resetFieldsAndClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" isLoading={restoreEnrolment.isPending}>
            <Trans>Restore this enrolment</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RestoreEnrolmentModal;
